import cn from "classnames";
import { useBoolean, useOnClickOutside } from "hooks";
import {
  CSSProperties,
  ReactNode,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import styles from "./InlineDropdown.module.scss";

export interface InlineDropdownOption<T extends string> {
  value: T;
  label: ReactNode;
}

interface Props<T extends string> {
  trigger: ReactNode;
  options: InlineDropdownOption<T>[];
  value?: T | null;
  align?: "left" | "right";
  disabled?: boolean;
  onSelect: (value: T) => void;
}

interface MenuCoords {
  top: number;
  left?: number;
  right?: number;
}

const InlineDropdown = <T extends string>({
  trigger,
  options,
  value,
  align = "left",
  disabled,
  onSelect,
}: Props<T>) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const { value: open, toggle, setFalse } = useBoolean(false);
  const [coords, setCoords] = useState<MenuCoords | null>(null);

  // Portal the menu to <body> with fixed positioning so it isn't clipped by
  // the table's `overflow-x: auto` wrapper. Re-measure on scroll/resize so it
  // stays anchored to the trigger while open.
  useLayoutEffect(() => {
    if (!open) return;

    const updateCoords = () => {
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (!rect) return;

      // Flip the menu above the trigger when there isn't enough room below it
      // (e.g. the trigger sits near the bottom of the viewport).
      const estMenuHeight = options.length * 36 + 12;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < estMenuHeight + 12 && rect.top > spaceBelow;
      const top = openUp ? rect.top - estMenuHeight - 6 : rect.bottom + 6;

      setCoords(
        align === "right"
          ? { top, right: window.innerWidth - rect.right }
          : { top, left: rect.left },
      );
    };

    updateCoords();
    window.addEventListener("scroll", updateCoords, true);
    window.addEventListener("resize", updateCoords);
    return () => {
      window.removeEventListener("scroll", updateCoords, true);
      window.removeEventListener("resize", updateCoords);
    };
  }, [open, align, options.length]);

  useOnClickOutside(menuRef, (event) => {
    // The trigger lives outside the portaled menu; let its own handler toggle.
    if (wrapperRef.current?.contains(event.target as Node)) return;
    setFalse();
  });

  const menuStyle: CSSProperties = coords
    ? {
        position: "fixed",
        top: coords.top,
        ...(coords.right !== undefined
          ? { right: coords.right, left: "auto" }
          : { left: coords.left }),
      }
    : {};

  return (
    <div
      className={styles.wrapper}
      ref={wrapperRef}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className={styles.trigger}
        disabled={disabled}
        onClick={() => !disabled && toggle()}
      >
        {trigger}
      </button>

      {open &&
        coords &&
        createPortal(
          <div
            ref={menuRef}
            className={styles.menu}
            style={menuStyle}
            onClick={(e) => e.stopPropagation()}
          >
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                className={cn(styles.option, {
                  [styles.optionActive]: option.value === value,
                })}
                onClick={() => {
                  setFalse();
                  if (option.value !== value) onSelect(option.value);
                }}
              >
                {option.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default InlineDropdown;
